import { Link, Locator } from '@readium/shared'
import { Highlighter, MessageSquare } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  deleteHighlight,
  getEpisodeHighlights,
  type Highlight,
  type MediaType,
  updateHighlight,
} from '@/api/services/highlight'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { cn } from '@/lib/utils'

import { GroupedItems, GroupedSectionList, SectionItem } from '../components/section-list'
import {
  countTocItems,
  findChapterForLocator,
  flattenToc,
  getPageFromLink,
  getPageFromLocator,
} from '../epub/components/toc-utils'
import { HIGHLIGHT_COLORS, type HighlightColor } from './highlight-colors'
import { HighlightSheet } from './highlight-sheet'

// 内部でfetchする場合
type FetchModeProps = {
  episodeId: number
  highlights?: never
  onNavigate: (highlightId: number) => void
  onHighlightSelect?: never
}

// 外部からhighlightsを渡す場合
type ControlledModeProps = {
  episodeId?: never
  highlights: Highlight[]
  onNavigate?: never
  onHighlightSelect: (highlight: Highlight) => void
}

type HighlightListDialogProps = {
  open: boolean
  onClose: () => void
  mediaType?: MediaType
  onHighlightsChange?: (highlights: Highlight[]) => void
  initialHighlightId?: number | null
  // EPUB用の追加props（Readium版）
  toc?: Link[]
  positions?: Locator[]
  numPages?: number
} & (FetchModeProps | ControlledModeProps)

export const HighlightListDialog = ({
  open,
  onClose,
  episodeId,
  highlights: externalHighlights,
  mediaType = 'image',
  onNavigate,
  onHighlightSelect,
  onHighlightsChange,
  initialHighlightId = null,
  toc = [],
  positions = [],
  numPages = 0,
}: HighlightListDialogProps) => {
  const [internalHighlights, setInternalHighlights] = useState<Highlight[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const highlights = externalHighlights ?? internalHighlights
  const updateHighlights = (newHighlights: Highlight[]) => {
    if (externalHighlights) {
      onHighlightsChange?.(newHighlights)
    } else {
      setInternalHighlights(newHighlights)
    }
  }

  const [editingHighlight, setEditingHighlight] = useState<Highlight | null>(null)

  const fetchHighlights = useCallback(async () => {
    if (!episodeId) return

    setIsLoading(true)
    try {
      const response = await getEpisodeHighlights(episodeId)
      setInternalHighlights(response)
      onHighlightsChange?.(response)
    } catch (error) {
      console.error('ハイライト取得エラー:', error)
    } finally {
      setIsLoading(false)
    }
  }, [episodeId, onHighlightsChange])

  useEffect(() => {
    if (open && episodeId) {
      fetchHighlights()
    }
  }, [open, episodeId, fetchHighlights])

  useEffect(() => {
    if (open && initialHighlightId && highlights.length > 0) {
      const highlight = highlights.find((h) => h.id === initialHighlightId)
      if (highlight) {
        setEditingHighlight(highlight)
      }
    }
  }, [open, initialHighlightId, highlights])

  const totalTocItems = useMemo(() => countTocItems(toc), [toc])

  const handleClick = useCallback(
    (highlight: Highlight) => {
      if (onHighlightSelect) {
        onHighlightSelect(highlight)
      } else if (onNavigate) {
        onNavigate(highlight.id)
      }
    },
    [onHighlightSelect, onNavigate],
  )

  /** ハイライトのlocatorをデシリアライズ */
  const getHighlightLocator = (highlight: Highlight): Locator | null => {
    const locatorData = highlight.position_data.locator
    if (!locatorData) return null
    return Locator.deserialize(locatorData) ?? null
  }

  /** ハイライトを章ごとにグループ化（EPUB用） */
  const groupedByChapter = useMemo((): GroupedItems<Highlight>[] => {
    if (mediaType !== 'epub' || toc.length === 0) {
      return [
        {
          section: { id: 'other', label: 'その他', href: '' },
          items: highlights.map((h) => ({
            id: h.id,
            content: h,
            onClick: () => handleClick(h),
          })),
        },
      ]
    }

    const flatToc = flattenToc(toc)
    const chapterMap = new Map<string, { chapter: Link; highlights: Highlight[] }>()
    const unknownHighlights: Highlight[] = []

    for (const highlight of highlights) {
      const locator = getHighlightLocator(highlight)
      if (!locator) {
        unknownHighlights.push(highlight)
        continue
      }

      const chapter = findChapterForLocator(locator, toc)
      if (chapter) {
        const key = chapter.href
        const existing = chapterMap.get(key)
        if (existing) {
          existing.highlights.push(highlight)
        } else {
          chapterMap.set(key, { chapter, highlights: [highlight] })
        }
      } else {
        unknownHighlights.push(highlight)
      }
    }

    const groups: GroupedItems<Highlight>[] = []
    let tocIndex = 0

    for (const navItem of flatToc) {
      const entry = chapterMap.get(navItem.href)
      if (entry && entry.highlights.length > 0) {
        const page = getPageFromLink(navItem, positions, numPages, tocIndex, totalTocItems)
        groups.push({
          section: {
            id: navItem.href,
            label: navItem.title ?? navItem.href,
            href: navItem.href,
            page,
          },
          items: entry.highlights.map((h) => ({
            id: h.id,
            content: h,
            onClick: () => handleClick(h),
          })),
        })
      }
      tocIndex++
    }

    if (unknownHighlights.length > 0) {
      groups.push({
        section: { id: 'unknown', label: 'その他', href: '' },
        items: unknownHighlights.map((h) => ({
          id: h.id,
          content: h,
          onClick: () => handleClick(h),
        })),
      })
    }

    return groups
  }, [highlights, mediaType, toc, positions, numPages, totalTocItems, handleClick])

  /** ページごとにグループ化 (image/pdf用) */
  const groupedByPage = useMemo((): GroupedItems<Highlight>[] => {
    if (mediaType === 'epub') return []

    const pageMap = new Map<number, Highlight[]>()

    for (const highlight of highlights) {
      const page =
        highlight.position_data.highlightAreas?.at(0)?.pageIndex ??
        highlight.position_data.selections?.at(0)?.page ??
        -1
      const existing = pageMap.get(page)
      if (existing) {
        existing.push(highlight)
      } else {
        pageMap.set(page, [highlight])
      }
    }

    const sortedPages = Array.from(pageMap.keys()).sort((a, b) => {
      if (a === -1) return 1
      if (b === -1) return -1
      return a - b
    })

    return sortedPages.map((page) => ({
      section: {
        id: String(page),
        label: page === -1 ? 'その他' : `${page + 1}ページ目`,
        href: '',
        page: page === -1 ? null : page + 1,
      },
      items: pageMap.get(page)!.map((h) => ({
        id: h.id,
        content: h,
        onClick: () => handleClick(h),
      })),
    }))
  }, [highlights, mediaType, handleClick])

  const groups = mediaType === 'epub' ? groupedByChapter : groupedByPage

  const handleEdit = (highlight: Highlight, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingHighlight(highlight)
  }

  const handleUpdate = async (id: number, color: HighlightColor, note: string) => {
    try {
      const updated = await updateHighlight(id, {
        color,
        note: note || undefined,
      })
      const newHighlights = highlights.map((h) => (h.id === updated.id ? updated : h))
      updateHighlights(newHighlights)
      setEditingHighlight(null)
    } catch (error) {
      console.error('ハイライト更新エラー:', error)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteHighlight(id)
      const newHighlights = highlights.filter((h) => h.id !== id)
      updateHighlights(newHighlights)
      setEditingHighlight(null)
    } catch (error) {
      console.error('ハイライト削除エラー:', error)
    }
  }

  const getColorClass = (color: string) => {
    return HIGHLIGHT_COLORS.find((c) => c.value === color)?.className ?? 'bg-yellow-400/60'
  }

  /** ハイライトのセクション名を取得 */
  const getSectionLabel = (highlight: Highlight): string | null => {
    if (mediaType !== 'epub') return null
    const locator = getHighlightLocator(highlight)
    if (!locator) return null
    const chapter = findChapterForLocator(locator, toc)
    return chapter?.title ?? null
  }

  /** ハイライトのページ番号を取得 */
  const getPageNumber = (highlight: Highlight): number | null => {
    if (mediaType === 'epub') {
      const locator = getHighlightLocator(highlight)
      return getPageFromLocator(locator, numPages)
    }
    const page = highlight.position_data.selections?.at(0)?.page
    return page !== undefined ? page + 1 : null
  }

  const renderHighlightItem = (item: SectionItem<Highlight>) => {
    const highlight = item.content
    const page = getPageNumber(highlight)
    const sectionLabel = getSectionLabel(highlight)

    return (
      <div
        key={highlight.id}
        onClick={item.onClick}
        className={cn(
          'w-full text-left px-4 py-3 hover:bg-accent transition-colors cursor-pointer',
          'focus:outline-none focus:bg-accent',
        )}
      >
        <div className="flex items-start gap-3">
          <div className={cn('w-3 h-3 rounded-full mt-1 shrink-0', getColorClass(highlight.color))} />
          <div className="flex-1 min-w-0">
            {(sectionLabel || page) && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                {sectionLabel && <span className="truncate">{sectionLabel}</span>}
                {sectionLabel && page && <span>·</span>}
                {page && <span className="shrink-0">p.{page}</span>}
              </div>
            )}
            {highlight.text && <p className="text-sm text-foreground line-clamp-2 mb-1">"{highlight.text}"</p>}
            {highlight.note && (
              <div className="flex items-start gap-1.5 mb-1">
                <MessageSquare size={14} className="text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground line-clamp-2">{highlight.note}</p>
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              <span>
                {new Date(highlight.created_at).toLocaleDateString('ja-JP', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          </div>
          <button
            className="shrink-0 p-2 hover:bg-accent/50 rounded-md transition-colors"
            onClick={(e) => handleEdit(highlight, e)}
          >
            <span className="text-xs text-muted-foreground">編集</span>
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <BottomSheet
        open={open}
        onOpenChange={(isOpen) => !isOpen && onClose()}
        title="ハイライト一覧"
        snapPoints={[0.6, 0.9]}
        defaultSnapPoint={0.6}
        className="max-h-[90vh]"
        dismissible
        noOverlay
      >
        <div className="px-4 pb-4">
          {!isLoading && highlights.length > 0 && (
            <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
              <Highlighter size={18} />
              <span>{highlights.length}件のハイライト</span>
            </div>
          )}
          {isLoading ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">読み込み中...</div>
          ) : (
            <GroupedSectionList
              groups={groups}
              emptyMessage={mediaType === 'epub' ? 'テキストを選択してハイライト' : '画像を長押しで選択できます'}
              renderItem={renderHighlightItem}
            />
          )}
        </div>
      </BottomSheet>

      <HighlightSheet
        open={!!editingHighlight}
        onClose={() => setEditingHighlight(null)}
        highlight={editingHighlight}
        onSave={() => {}}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />
    </>
  )
}
